import { Button } from "@/components/ui/button";
import { EnhancedHeader } from "@/components/ui";
import {
  Palette,
  Info,
  ExternalLink,
  ShieldCheck,
  FileText,
  FileLock,
  Mail,
  KeyRound,
  Code,
  Trash2,
  ChevronRight,
} from "lucide-react";
import { useLocation } from "wouter";
import { useThemeEffect } from "@/hooks/use-theme";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";

export default function UserSettingsPage() {
  const [_, setLocation] = useLocation();
  const { user, needsVerification, resendVerificationEmail } = useAuth();

  // Apply theme when component mounts
  useThemeEffect();

  // Handle resend verification email
  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail.mutateAsync();
      toast({
        title: "Verification Email Sent",
        description: "Please check your inbox for the verification link.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          "Failed to send verification email. Please try again later.",
        variant: "destructive",
      });
      console.error("Resend verification error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      <EnhancedHeader
        title="Settings"
        backPath="/profile"
        coloredText={true}
        rightContent={null}
        showBackButton={true}
      />

      <div className="max-w-md mx-auto">
        {/* Email Verification Alert (if needed) */}
        {needsVerification && (
          <div className="mx-4 mb-5 p-4 rounded-lg bg-yellow-950/50 border border-yellow-500/50">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-5 w-5 text-yellow-400 flex-shrink-0" />
              <h3 className="font-medium">Verify Email</h3>
            </div>
            <p className="text-sm text-gray-300 mb-3">
              You need to verify {user?.email} to access all features
            </p>
            <Button
              onClick={handleResendVerification}
              variant="outline"
              size="sm"
              className="border-yellow-500/50 text-yellow-300 hover:bg-yellow-950/50 w-full"
              disabled={resendVerificationEmail.isPending}
            >
              {resendVerificationEmail.isPending
                ? "Sending..."
                : "Resend Verification Email"}
            </Button>
          </div>
        )}

        {/* Account Settings Section */}
        <div className="bg-zinc-900 rounded-xl overflow-hidden mb-4 mx-4">
          <h3 className="text-xs uppercase text-gray-400 font-medium px-4 pt-3 pb-1">
            Account Settings
          </h3>
          
          {/* Email Info */}
          <div className="px-4 py-3 border-b border-zinc-800">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-xs text-gray-400">{user?.email || "No email set"}</p>
                </div>
              </div>
              {user?.isVerified ? (
                <Badge className="bg-green-900/50 text-green-300 border-green-500/50 text-xs">
                  Verified
                </Badge>
              ) : (
                <Badge className="bg-yellow-900/50 text-yellow-300 border-yellow-500/50 text-xs">
                  Unverified
                </Badge>
              )}
            </div>
          </div>
          
          {/* Email Change */}
          <div 
            onClick={() => setLocation("/email-change")}
            className="flex items-center justify-between p-4 border-b border-zinc-800 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-400" />
              </div>
              <span className="font-medium">Change Email</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
          
          {/* Password Change */}
          <div 
            onClick={() => setLocation("/password-change")}
            className="flex items-center justify-between p-4 border-b border-zinc-800 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <KeyRound className="h-5 w-5 text-blue-400" />
              </div>
              <span className="font-medium">Change Password</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
          
          {/* Support */}
          <div 
            onClick={() => setLocation("/support")}
            className="flex items-center justify-between p-4 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <Info className="h-5 w-5 text-blue-400" />
              </div>
              <span className="font-medium">Support & Bugs</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
        </div>

        {/* Game Settings Section */}
        <div className="bg-zinc-900 rounded-xl overflow-hidden mb-4 mx-4">
          <h3 className="text-xs uppercase text-gray-400 font-medium px-4 pt-3 pb-1">
            Game Settings
          </h3>
          
          {/* Game Themes */}
          <div 
            onClick={() => setLocation("/settings/themes")}
            className="flex items-center justify-between p-4 border-b border-zinc-800 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <Palette className="h-5 w-5 text-purple-400" />
              </div>
              <span className="font-medium">Game Themes</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
        </div>

        {/* Information Section */}
        <div className="bg-zinc-900 rounded-xl overflow-hidden mb-4 mx-4">
          <h3 className="text-xs uppercase text-gray-400 font-medium px-4 pt-3 pb-1">
            Information
          </h3>
          
          {/* About */}
          <div 
            onClick={() => setLocation("/about")}
            className="flex items-center justify-between p-4 border-b border-zinc-800 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <Info className="h-5 w-5 text-green-400" />
              </div>
              <span className="font-medium">About Spar Legends</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
          
          {/* Changelog */}
          <div 
            onClick={() => setLocation("/changelog")}
            className="flex items-center justify-between p-4 border-b border-zinc-800 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <ExternalLink className="h-5 w-5 text-green-400" />
              </div>
              <span className="font-medium">Change Log</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
          
          {/* Privacy Policy */}
          <div 
            onClick={() => setLocation("/privacy-policy")}
            className="flex items-center justify-between p-4 border-b border-zinc-800 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <FileLock className="h-5 w-5 text-green-400" />
              </div>
              <span className="font-medium">Privacy Policy</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
          
          {/* Terms of Service */}
          <div 
            onClick={() => setLocation("/terms-of-service")}
            className="flex items-center justify-between p-4 border-b border-zinc-800 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <FileText className="h-5 w-5 text-green-400" />
              </div>
              <span className="font-medium">Terms of Service</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
          
          {/* Open Source Credits */}
          <div 
            onClick={() => setLocation("/open-source-credits")}
            className="flex items-center justify-between p-4 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <Code className="h-5 w-5 text-green-400" />
              </div>
              <span className="font-medium">Open Source Credits</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-zinc-900 rounded-xl overflow-hidden mb-4 mx-4">
          <h3 className="text-xs uppercase text-red-400 font-medium px-4 pt-3 pb-1">
            Danger Zone
          </h3>
          
          {/* Delete Account */}
          <div 
            onClick={() => setLocation("/delete-account")}
            className="flex items-center justify-between p-4 hover:bg-zinc-800 active:bg-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-red-950/50 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-400" />
              </div>
              <span className="font-medium text-red-400">Delete Account</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </div>
        </div>
      </div>
    </div>
  );
}
